import React, { useState } from 'react';
import { Form, Field } from 'react-final-form';
import type { ModalRequiredProps } from '../../../components/Toolkit/Modal/BaseModal';
import { TextArea } from '../../../components/Toolkit/Inputs/TextArea';
import { Button } from '../../../components/Toolkit/Button/Button';
import {
  required,
  isEmail,
  composeValidators,
} from '../../../helpers/InputValidators';
import { handleErrorMessage } from '../../../helpers/forms';
import { Link } from '../../../components/Toolkit/Button/Link';
import * as Styled from '../../../components/Toolkit/ReportAdModal/ReportAdModal.styled';
import { pushToDataLayer } from '../../../services/gtmService';

export interface ReportAdModalProps extends ModalRequiredProps {
  flagImageUrl: string;
  onReportAd: (
    value: ReportAdSubmissionValues,
  ) => Promise<{ [key: string]: any } | void>;
  onClose: () => void;
  view?: 'INITIAL' | 'SUCCESS';
  categories: Category[];
  isUserLoggedIn: boolean;
  className?: string;
}

export interface ReportAdSubmissionValues {
  category: Category;
  email?: string;
  name?: string;
  reason: string;
  consent: boolean;
}

type Category = {
  id: number;
  description: string;
};

interface Values {
  email?: string;
  name?: string;
  comment: string;
  select: string;
}

function ReportAdModal({
  isOpen,
  askToClose,
  flagImageUrl,
  onReportAd,
  onClose,
  view = 'INITIAL',
  categories,
  isUserLoggedIn,
}: ReportAdModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function onSubmit(values: Values) {
    setIsSubmitting(true);
    const reason = categories.filter(
      (category) => category.description === values.select,
    );
    const category = reason[0];
    const updatedValues: ReportAdSubmissionValues = {
      category,
      consent: true,
      name: values.name,
      reason: values.comment,
      email: values.email,
    };
    const response = await onReportAd(updatedValues);
    setIsSubmitting(false);
    return response;
  }

  function mapCategories(entry: Category[]) {
    const entries = entry.map((entry) => entry.description);
    return ['Select a reason', ...entries];
  }

  function handleClose() {
    onClose();
    askToClose();
  }

  const getReportAdLink = () => {
    const brand = 'donedeal_dsa';
    const adUrl = location.href;
    return `https://distilleddsa.zendesk.com/hc/en-us/requests/new?tf_21975549429521=${brand}&tf_21927903033105=${adUrl}`;
  };

  const handleReportLinkClick = () => {
    pushToDataLayer({
      event: 'internal_click',
      event_category: 'ad_features',
      event_action: 'report_illegal_content',
      event_label: 'report illegal content link',
    });
    handleClose();
  };

  return (
    <Styled.BaseModal
      isOpen={isOpen}
      askToClose={askToClose}
      testId="report-ad-modal"
    >
      <Styled.Header
        illustration={flagImageUrl}
        title="Report Ad"
        listItems={['Help us understand the issue']}
      />
      {view === 'INITIAL' ? (
        <Form
          onSubmit={onSubmit}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} noValidate>
              {!isUserLoggedIn && (
                <>
                  <Field name="name" validate={required('Name Required')}>
                    {({ input, meta }) => (
                      <Styled.Input
                        type="text"
                        name={input.name}
                        value={input.value}
                        onChange={input.onChange}
                        errorMessage={handleErrorMessage(meta)}
                        label="Your Name"
                      />
                    )}
                  </Field>
                  <Field
                    name="email"
                    validate={composeValidators(
                      required('Email Required'),
                      isEmail,
                    )}
                  >
                    {({ input, meta }) => (
                      <Styled.Input
                        type="email"
                        name={input.name}
                        value={input.value}
                        onChange={input.onChange}
                        errorMessage={handleErrorMessage(meta)}
                        label="Your Email"
                      />
                    )}
                  </Field>
                </>
              )}
              {categories && (
                <Field name="select" validate={required('Choice Required')}>
                  {({ input, meta }) => (
                    <Styled.SSelect
                      value={input.value}
                      onChange={input.onChange}
                      errorMessage={handleErrorMessage(meta)}
                      id="?"
                      name={input.name}
                      placeholder={true}
                      options={mapCategories(categories)}
                      label="Select a reason"
                    />
                  )}
                </Field>
              )}
              <Field name="comment" validate={required('Comment Required')}>
                {({ input, meta }) => (
                  <TextArea
                    name={input.name}
                    value={input.value}
                    onChange={input.onChange}
                    errorMessage={handleErrorMessage(meta)}
                    label="Your Comment"
                  />
                )}
              </Field>
              <Styled.Legal>
                By selecting Agree & Send below, I agree to the DoneDeal{' '}
                <a
                  href="https://hello.donedeal.ie/hc/en-us/articles/201251761-Terms-Conditions-of-Use-of-DoneDeal"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Use
                </a>{' '}
                and{' '}
                <a
                  href="https://hello.donedeal.ie/hc/en-us/articles/201251721-Privacy-Policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>
              </Styled.Legal>

              {!isSubmitting ? (
                <Button type="submit" data-testid="send">
                  Agree & Send
                </Button>
              ) : (
                <Button>Submitting...</Button>
              )}

              <Styled.SpacedDivider> or </Styled.SpacedDivider>

              <Link
                href={getReportAdLink()}
                ofType="SECONDARY"
                target="_blank"
                centerAll={true}
                onClick={handleReportLinkClick}
              >
                Report Illegal Content
                <Styled.SOpenOutlineIc width="16" height="16" />
              </Link>

              <Styled.AdditionalInfoText>
                Use the above link to report “illegal content” under the Digital
                Services Act. In order to do this your report must be specific
                and include an explanation outlining why the content is illegal.
              </Styled.AdditionalInfoText>
            </form>
          )}
        />
      ) : (
        <>
          <Styled.SNotification intent="SUCCESS">
            Ad Reported Successfully
          </Styled.SNotification>
          <Styled.TakeAction>
            Thanks, we will investigate this ad and take the appropriate action.
          </Styled.TakeAction>
          <Button onClick={handleClose} data-testid="ok">
            Okay
          </Button>
        </>
      )}
    </Styled.BaseModal>
  );
}

export { ReportAdModal };
